import { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { CommandPalette } from "@/components/CommandPalette";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { lazyWithRetry } from "@/lib/lazyWithRetry";

const LoadingRoute = () => (
  <div className="min-h-[50vh] grid place-items-center">
    <div className="text-sm text-muted-foreground">Loading…</div>
  </div>
);

// Marketing Pages (lazy-loaded for faster first paint)
const Index = lazyWithRetry(() => import("./pages/Index"));
const Platform = lazyWithRetry(() => import("./pages/Platform"));
const Security = lazyWithRetry(() => import("./pages/Security"));
const Pricing = lazyWithRetry(() => import("./pages/Pricing"));
const Demo = lazyWithRetry(() => import("./pages/Demo"));
const GptStore = lazyWithRetry(() => import("./pages/GptStore"));
const Privacy = lazyWithRetry(() => import("./pages/Privacy"));
const Terms = lazyWithRetry(() => import("./pages/Terms"));
const Cookies = lazyWithRetry(() => import("./pages/Cookies"));
const Login = lazyWithRetry(() => import("./pages/Login"));
const Signup = lazyWithRetry(() => import("./pages/Signup"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const About = lazyWithRetry(() => import("./pages/About"));
const Careers = lazyWithRetry(() => import("./pages/Careers"));
const Contact = lazyWithRetry(() => import("./pages/Contact"));
const Blog = lazyWithRetry(() => import("./pages/Blog"));
const Docs = lazyWithRetry(() => import("./pages/Docs"));
const ApiReference = lazyWithRetry(() => import("./pages/ApiReference"));
const Support = lazyWithRetry(() => import("./pages/Support"));
const Status = lazyWithRetry(() => import("./pages/Status"));
const Compliance = lazyWithRetry(() => import("./pages/Compliance"));
const ForgotPassword = lazyWithRetry(() => import("./pages/ForgotPassword"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const Install = lazyWithRetry(() => import("./pages/Install"));
const SharedFile = lazyWithRetry(() => import("./pages/SharedFile"));
const BlogPost = lazyWithRetry(() => import("./pages/BlogPost"));
const Investors = lazyWithRetry(() => import("./pages/Investors"));
const Partners = lazyWithRetry(() => import("./pages/Partners"));
const Roadmap = lazyWithRetry(() => import("./pages/Roadmap"));
const Dpa = lazyWithRetry(() => import("./pages/Dpa"));
const AttorneyGeneral = lazyWithRetry(() => import("./pages/industries/AttorneyGeneral"));
const LawEnforcement = lazyWithRetry(() => import("./pages/industries/LawEnforcement"));
const Healthcare = lazyWithRetry(() => import("./pages/industries/Healthcare"));
const Education = lazyWithRetry(() => import("./pages/industries/Education"));
const CourtsADR = lazyWithRetry(() => import("./pages/solutions/CourtsADR"));
const PoliceOversight = lazyWithRetry(() => import("./pages/solutions/PoliceOversight"));
const InsuranceDisputes = lazyWithRetry(() => import("./pages/solutions/InsuranceDisputes"));
const CorporateComplaints = lazyWithRetry(() => import("./pages/solutions/CorporateComplaints"));
const Procurement = lazyWithRetry(() => import("./pages/Procurement"));
const WhyLexiGov = lazyWithRetry(() => import("./pages/WhyLexiGov"));

// App Pages (lazy-loaded to avoid pulling heavy dependencies into marketing bundle)
const Dashboard = lazyWithRetry(() => import("./pages/app/Dashboard"));
const CasesList = lazyWithRetry(() => import("./pages/app/CasesList"));
const NewCase = lazyWithRetry(() => import("./pages/app/NewCase"));
const CaseDetail = lazyWithRetry(() => import("./pages/app/CaseDetail"));
const Documents = lazyWithRetry(() => import("./pages/app/Documents"));
const Tasks = lazyWithRetry(() => import("./pages/app/Tasks"));
const Admin = lazyWithRetry(() => import("./pages/app/Admin"));
const AdminUsers = lazyWithRetry(() => import("./pages/app/AdminUsers"));
const AdminAudit = lazyWithRetry(() => import("./pages/app/AdminAudit"));
const ProfileSettings = lazyWithRetry(() => import("./pages/app/ProfileSettings"));
const Reports = lazyWithRetry(() => import("./pages/app/Reports"));
const SecurityDashboard = lazyWithRetry(() => import("./pages/app/SecurityDashboard"));
const RfpManager = lazyWithRetry(() => import("./pages/app/RfpManager"));
const RfpDocuments = lazyWithRetry(() => import("./pages/app/RfpDocuments"));
const Templates = lazyWithRetry(() => import("./pages/app/Templates"));
const Locations = lazyWithRetry(() => import("./pages/app/Locations"));
const Properties = lazyWithRetry(() => import("./pages/app/Properties"));
const DataMigration = lazyWithRetry(() => import("./pages/app/DataMigration"));
const MigrationJobs = lazyWithRetry(() => import("./pages/app/MigrationJobs"));
const CJISCompliance = lazyWithRetry(() => import("./pages/app/CJISCompliance"));
const Calendar = lazyWithRetry(() => import("./pages/app/Calendar"));
const Workflows = lazyWithRetry(() => import("./pages/app/Workflows"));
const Billing = lazyWithRetry(() => import("./pages/app/Billing"));
const SupportTickets = lazyWithRetry(() => import("./pages/app/SupportTickets"));
const KnowledgeBase = lazyWithRetry(() => import("./pages/app/KnowledgeBase"));
const Integrations = lazyWithRetry(() => import("./pages/app/Integrations"));
const Analytics = lazyWithRetry(() => import("./pages/app/Analytics"));
const FormBuilderPage = lazyWithRetry(() => import("./pages/app/FormBuilderPage"));
const AdminPlans = lazyWithRetry(() => import("./pages/app/AdminPlans"));
const AdminStats = lazyWithRetry(() => import("./pages/app/AdminStats"));
const AdminNewsletter = lazyWithRetry(() => import("./pages/app/AdminNewsletter"));
const AdminFeatures = lazyWithRetry(() => import("./pages/app/AdminFeatures"));
const PersonIndex = lazyWithRetry(() => import("./pages/app/PersonIndex"));
const OrganizationIndex = lazyWithRetry(() => import("./pages/app/OrganizationIndex"));
const OagEcmRfp = lazyWithRetry(() => import("./pages/app/OagEcmRfp"));
const SnugCmsRfq = lazyWithRetry(() => import("./pages/app/SnugCmsRfq"));
const RecordsManagement = lazyWithRetry(() => import("./pages/app/RecordsManagement"));
const FoilRequests = lazyWithRetry(() => import("./pages/app/FoilRequests"));
const ApiGovernance = lazyWithRetry(() => import("./pages/app/ApiGovernance"));
const AccessibilityDashboard = lazyWithRetry(() => import("./pages/app/AccessibilityDashboard"));
const IngestionGovernance = lazyWithRetry(() => import("./pages/app/IngestionGovernance"));
const IntegrityVerification = lazyWithRetry(() => import("./pages/app/IntegrityVerification"));
const OutreachOperations = lazyWithRetry(() => import("./pages/app/OutreachOperations"));
const ClinicalServices = lazyWithRetry(() => import("./pages/app/ClinicalServices"));
const ReviewQueue = lazyWithRetry(() => import("./pages/app/ReviewQueue"));
const HospitalServices = lazyWithRetry(() => import("./pages/app/HospitalServices"));
const BillingGrants = lazyWithRetry(() => import("./pages/app/BillingGrants"));
const AIEthics = lazyWithRetry(() => import("./pages/app/AIEthics"));
const QuantumShieldIntegration = lazyWithRetry(() => import("./pages/app/QuantumShieldIntegration"));
const MFASetup = lazyWithRetry(() => import("./pages/app/MFASetup"));
const AdminPermissions = lazyWithRetry(() => import("./pages/app/AdminPermissions"));
const EvidenceLocker = lazyWithRetry(() => import("./pages/app/EvidenceLocker"));
const GovernanceDashboard = lazyWithRetry(() => import("./pages/app/GovernanceDashboard"));
const DataPortability = lazyWithRetry(() => import("./pages/app/DataPortability"));
const AdminCatalog = lazyWithRetry(() => import("./pages/app/AdminCatalog"));
const AdminPricing = lazyWithRetry(() => import("./pages/app/AdminPricing"));
const AdminContracts = lazyWithRetry(() => import("./pages/app/AdminContracts"));
const AdminPartners = lazyWithRetry(() => import("./pages/app/AdminPartners"));
const AgencySecurityReport = lazyWithRetry(() => import("./pages/app/AgencySecurityReport"));
const DisputesList = lazyWithRetry(() => import("./pages/app/DisputesList"));
const NewDispute = lazyWithRetry(() => import("./pages/app/NewDispute"));
const DisputeDetail = lazyWithRetry(() => import("./pages/app/DisputeDetail"));
const DisputeAnalytics = lazyWithRetry(() => import("./pages/app/DisputeAnalytics"));
const MediatorRoster = lazyWithRetry(() => import("./pages/app/MediatorRoster"));
const DisputePrograms = lazyWithRetry(() => import("./pages/app/DisputePrograms"));
const OutcomeIntelligence = lazyWithRetry(() => import("./pages/app/OutcomeIntelligence"));
const DataGovernanceDashboard = lazyWithRetry(() => import("./pages/app/DataGovernanceDashboard"));
const MediatorAvailability = lazyWithRetry(() => import("./pages/app/MediatorAvailability"));
const ADROperationsDashboard = lazyWithRetry(() => import("./pages/app/ADROperationsDashboard"));
const DisputeKnowledge = lazyWithRetry(() => import("./pages/app/DisputeKnowledge"));
const DisputeIntelligence = lazyWithRetry(() => import("./pages/app/DisputeIntelligence"));
const IntegrationHub = lazyWithRetry(() => import("./pages/app/IntegrationHub"));
const PublishingDistribution = lazyWithRetry(() => import("./pages/app/PublishingDistribution"));
const OperationsDashboard = lazyWithRetry(() => import("./pages/app/OperationsDashboard"));
const IntakeQueue = lazyWithRetry(() => import("./pages/app/IntakeQueue"));
const BreakGlassPage = lazyWithRetry(() => import("./pages/app/BreakGlassPage"));
const ExternalPortalPage = lazyWithRetry(() => import("./pages/ExternalPortalPage"));
const SnugDashboard = lazyWithRetry(() => import("./pages/app/SnugDashboard"));
const DisputeDashboard = lazyWithRetry(() => import("./pages/app/DisputeDashboard"));

// Legal Services Module (feature-flag gated)
const LegalClients = lazyWithRetry(() => import("./pages/app/legal/LegalClients"));
const LegalMatters = lazyWithRetry(() => import("./pages/app/legal/LegalMatters"));
const LegalIntake = lazyWithRetry(() => import("./pages/app/legal/LegalIntake"));
const LegalIntakeBuilder = lazyWithRetry(() => import("./pages/app/legal/LegalIntakeBuilder"));
const LegalMatterDetail = lazyWithRetry(() => import("./pages/app/legal/LegalMatterDetail"));
const LegalAnalytics = lazyWithRetry(() => import("./pages/app/legal/LegalAnalytics"));
const LegalTemplates = lazyWithRetry(() => import("./pages/app/legal/LegalTemplates"));
const LegalTime = lazyWithRetry(() => import("./pages/app/legal/LegalTime"));
const LegalSupervisor = lazyWithRetry(() => import("./pages/app/legal/LegalSupervisor"));
const LegalPortalAdmin = lazyWithRetry(() => import("./pages/app/legal/LegalPortalAdmin"));
const LegalOutcomes = lazyWithRetry(() => import("./pages/app/legal/LegalOutcomes"));
const LegalDocIntelligence = lazyWithRetry(() => import("./pages/app/legal/LegalDocIntelligence"));
const LegalTimePayroll = lazyWithRetry(() => import("./pages/app/legal/LegalTimePayroll"));
const DoccsLookup = lazyWithRetry(() => import("./pages/app/legal/DoccsLookup"));
const HandwrittenNotes = lazyWithRetry(() => import("./pages/app/legal/HandwrittenNotes"));
const AdoptionCenter = lazyWithRetry(() => import("./pages/app/AdoptionCenter"));
const AdoptionPackage = lazyWithRetry(() => import("./pages/AdoptionPackage"));
const SupportTiers = lazyWithRetry(() => import("./pages/SupportTiers"));
const M365Admin = lazyWithRetry(() => import("./pages/app/admin/M365Admin"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: (failureCount, error) => {
        // Don't retry on 4xx client errors (auth failures, not found, etc.)
        if (error instanceof Error && /4\d{2}/.test(error.message)) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <CommandPalette />
          <ErrorBoundary>
            <Suspense fallback={<LoadingRoute />}>
              <Routes>
                {/* Marketing Routes */}
                <Route path="/" element={<Index />} />
                <Route path="/platform" element={<Platform />} />
                <Route path="/security" element={<Security />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/demo" element={<Demo />} />
                <Route path="/gpt-store" element={<GptStore />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/cookies" element={<Cookies />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/about" element={<About />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/docs" element={<Docs />} />
                <Route path="/api" element={<ApiReference />} />
                <Route path="/support" element={<Support />} />
                <Route path="/status" element={<Status />} />
                <Route path="/compliance" element={<Compliance />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/install" element={<Install />} />
                <Route path="/shared/:token" element={<SharedFile />} />
                <Route path="/investors" element={<Investors />} />
                <Route path="/partners" element={<Partners />} />
                <Route path="/roadmap" element={<Roadmap />} />
                <Route path="/dpa" element={<Dpa />} />
                <Route path="/industries/attorney-general" element={<AttorneyGeneral />} />
                <Route path="/industries/law-enforcement" element={<LawEnforcement />} />
                <Route path="/industries/healthcare" element={<Healthcare />} />
                <Route path="/industries/education" element={<Education />} />
                <Route path="/solutions/courts-adr" element={<CourtsADR />} />
                <Route path="/solutions/police-oversight" element={<PoliceOversight />} />
                <Route path="/solutions/insurance-disputes" element={<InsuranceDisputes />} />
                <Route path="/solutions/corporate-complaints" element={<CorporateComplaints />} />
                <Route path="/procurement" element={<Procurement />} />
                <Route path="/adoption" element={<AdoptionPackage />} />
                <Route path="/support-tiers" element={<SupportTiers />} />
                <Route path="/why-lexigov" element={<WhyLexiGov />} />

                {/* Protected App Routes */}
                <Route path="/app" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/app/cases" element={<ProtectedRoute><CasesList /></ProtectedRoute>} />
                <Route path="/app/cases/new" element={<ProtectedRoute><NewCase /></ProtectedRoute>} />
                <Route path="/app/cases/:id" element={<ProtectedRoute><CaseDetail /></ProtectedRoute>} />
                <Route path="/app/documents" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
                <Route path="/app/tasks" element={<ProtectedRoute><Tasks /></ProtectedRoute>} />
                <Route path="/app/admin" element={<ProtectedRoute requireAdmin><Admin /></ProtectedRoute>} />
                <Route path="/app/admin/users" element={<ProtectedRoute requireAdmin><AdminUsers /></ProtectedRoute>} />
                <Route path="/app/admin/audit" element={<ProtectedRoute requireAdmin><AdminAudit /></ProtectedRoute>} />
                <Route path="/app/admin/security" element={<ProtectedRoute requireAdmin><SecurityDashboard /></ProtectedRoute>} />
                <Route path="/app/reports" element={<ProtectedRoute requireAdmin><Reports /></ProtectedRoute>} />
                <Route path="/app/rfp" element={<ProtectedRoute requireAdmin><RfpManager /></ProtectedRoute>} />
                <Route path="/app/rfp/documents" element={<ProtectedRoute requireAdmin><RfpDocuments /></ProtectedRoute>} />
                <Route path="/app/templates" element={<ProtectedRoute><Templates /></ProtectedRoute>} />
                <Route path="/app/locations" element={<ProtectedRoute><Locations /></ProtectedRoute>} />
                <Route path="/app/properties" element={<ProtectedRoute><Properties /></ProtectedRoute>} />
                <Route path="/app/admin/migration" element={<ProtectedRoute requireAdmin><DataMigration /></ProtectedRoute>} />
                <Route path="/app/admin/migration/jobs" element={<ProtectedRoute requireAdmin><MigrationJobs /></ProtectedRoute>} />
                <Route path="/app/admin/cjis" element={<ProtectedRoute requireAdmin><CJISCompliance /></ProtectedRoute>} />
                <Route path="/app/profile" element={<ProtectedRoute><ProfileSettings /></ProtectedRoute>} />
                <Route path="/app/calendar" element={<ProtectedRoute><Calendar /></ProtectedRoute>} />
                <Route path="/app/workflows" element={<ProtectedRoute requireAdmin><Workflows /></ProtectedRoute>} />
                <Route path="/app/analytics" element={<ProtectedRoute requireAdmin><Analytics /></ProtectedRoute>} />
                <Route path="/app/billing" element={<ProtectedRoute requireAdmin><Billing /></ProtectedRoute>} />
                <Route path="/app/support-tickets" element={<ProtectedRoute><SupportTickets /></ProtectedRoute>} />
                <Route path="/app/knowledge-base" element={<ProtectedRoute><KnowledgeBase /></ProtectedRoute>} />
                <Route path="/app/integrations" element={<ProtectedRoute requireAdmin><Integrations /></ProtectedRoute>} />
                <Route path="/app/forms" element={<ProtectedRoute requireAdmin><FormBuilderPage /></ProtectedRoute>} />
                <Route path="/app/admin/plans" element={<ProtectedRoute requireAdmin><AdminPlans /></ProtectedRoute>} />
                <Route path="/app/admin/stats" element={<ProtectedRoute requireAdmin><AdminStats /></ProtectedRoute>} />
                <Route path="/app/admin/newsletter" element={<ProtectedRoute requireAdmin><AdminNewsletter /></ProtectedRoute>} />
                <Route path="/app/admin/features" element={<ProtectedRoute requireAdmin><AdminFeatures /></ProtectedRoute>} />
                <Route path="/app/persons" element={<ProtectedRoute><PersonIndex /></ProtectedRoute>} />
                <Route path="/app/organizations" element={<ProtectedRoute><OrganizationIndex /></ProtectedRoute>} />
                <Route path="/app/rfp/oag-ecm" element={<ProtectedRoute requireAdmin><OagEcmRfp /></ProtectedRoute>} />
                <Route path="/app/rfp/snug-cms" element={<ProtectedRoute requireAdmin><SnugCmsRfq /></ProtectedRoute>} />
                <Route path="/app/records" element={<ProtectedRoute><RecordsManagement /></ProtectedRoute>} />
                <Route path="/app/foil" element={<ProtectedRoute><FoilRequests /></ProtectedRoute>} />
                <Route path="/app/api-governance" element={<ProtectedRoute requireAdmin><ApiGovernance /></ProtectedRoute>} />
                <Route path="/app/accessibility" element={<ProtectedRoute requireAdmin><AccessibilityDashboard /></ProtectedRoute>} />
                <Route path="/app/ingestion" element={<ProtectedRoute requireAdmin><IngestionGovernance /></ProtectedRoute>} />
                <Route path="/app/integrity" element={<ProtectedRoute requireAdmin><IntegrityVerification /></ProtectedRoute>} />
                <Route path="/app/outreach" element={<ProtectedRoute><OutreachOperations /></ProtectedRoute>} />
                <Route path="/app/clinical" element={<ProtectedRoute><ClinicalServices /></ProtectedRoute>} />
                <Route path="/app/review-queue" element={<ProtectedRoute><ReviewQueue /></ProtectedRoute>} />
                <Route path="/app/hospital" element={<ProtectedRoute><HospitalServices /></ProtectedRoute>} />
                <Route path="/app/billing-grants" element={<ProtectedRoute requireAdmin><BillingGrants /></ProtectedRoute>} />
                <Route path="/app/ai-ethics" element={<ProtectedRoute requireAdmin><AIEthics /></ProtectedRoute>} />
                <Route path="/app/admin/quantumshield" element={<ProtectedRoute requireAdmin><QuantumShieldIntegration /></ProtectedRoute>} />
                <Route path="/app/admin/permissions" element={<ProtectedRoute requireAdmin><AdminPermissions /></ProtectedRoute>} />
                <Route path="/app/cases/:caseId/evidence" element={<ProtectedRoute><EvidenceLocker /></ProtectedRoute>} />
                <Route path="/app/mfa-setup" element={<ProtectedRoute><MFASetup /></ProtectedRoute>} />
                <Route path="/app/admin/governance" element={<ProtectedRoute requireAdmin><GovernanceDashboard /></ProtectedRoute>} />
                <Route path="/app/admin/data-portability" element={<ProtectedRoute requireAdmin><DataPortability /></ProtectedRoute>} />
                <Route path="/app/admin/catalog" element={<ProtectedRoute requireAdmin><AdminCatalog /></ProtectedRoute>} />
                <Route path="/app/admin/pricing" element={<ProtectedRoute requireAdmin><AdminPricing /></ProtectedRoute>} />
                <Route path="/app/admin/contracts" element={<ProtectedRoute requireAdmin><AdminContracts /></ProtectedRoute>} />
                <Route path="/app/admin/partners" element={<ProtectedRoute requireAdmin><AdminPartners /></ProtectedRoute>} />
                <Route path="/app/admin/agency-report" element={<ProtectedRoute requireAdmin><AgencySecurityReport /></ProtectedRoute>} />
                <Route path="/app/disputes" element={<ProtectedRoute><DisputesList /></ProtectedRoute>} />
                <Route path="/app/disputes/new" element={<ProtectedRoute><NewDispute /></ProtectedRoute>} />
                <Route path="/app/disputes/:id" element={<ProtectedRoute><DisputeDetail /></ProtectedRoute>} />
                <Route path="/app/dispute-analytics" element={<ProtectedRoute><DisputeAnalytics /></ProtectedRoute>} />
                <Route path="/app/mediator-roster" element={<ProtectedRoute><MediatorRoster /></ProtectedRoute>} />
                <Route path="/app/dispute-programs" element={<ProtectedRoute requireAdmin><DisputePrograms /></ProtectedRoute>} />
                <Route path="/app/outcome-intelligence" element={<ProtectedRoute><OutcomeIntelligence /></ProtectedRoute>} />
                <Route path="/app/mediator-availability" element={<ProtectedRoute><MediatorAvailability /></ProtectedRoute>} />
                <Route path="/app/adr-operations" element={<ProtectedRoute requireAdmin><ADROperationsDashboard /></ProtectedRoute>} />
                <Route path="/app/dispute-knowledge" element={<ProtectedRoute><DisputeKnowledge /></ProtectedRoute>} />
                <Route path="/app/dispute-intelligence" element={<ProtectedRoute requireAdmin><DisputeIntelligence /></ProtectedRoute>} />
                <Route path="/app/admin/data-governance" element={<ProtectedRoute requireAdmin><DataGovernanceDashboard /></ProtectedRoute>} />
                <Route path="/app/admin/integrations-hub" element={<ProtectedRoute requireAdmin><IntegrationHub /></ProtectedRoute>} />
                <Route path="/app/admin/publishing-distribution" element={<ProtectedRoute requireAdmin><PublishingDistribution /></ProtectedRoute>} />
                <Route path="/app/admin/operations" element={<ProtectedRoute requireAdmin><OperationsDashboard /></ProtectedRoute>} />
                <Route path="/app/intake-queue" element={<ProtectedRoute><IntakeQueue /></ProtectedRoute>} />
                <Route path="/app/admin/break-glass" element={<ProtectedRoute requireAdmin><BreakGlassPage /></ProtectedRoute>} />
                <Route path="/app/snug-dashboard" element={<ProtectedRoute><SnugDashboard /></ProtectedRoute>} />
                <Route path="/app/dispute-dashboard" element={<ProtectedRoute><DisputeDashboard /></ProtectedRoute>} />

                {/* Legal Services Module (gated by feature flag in DB + UI) */}
                <Route path="/app/legal/clients" element={<ProtectedRoute><LegalClients /></ProtectedRoute>} />
                <Route path="/app/legal/matters" element={<ProtectedRoute><LegalMatters /></ProtectedRoute>} />
                <Route path="/app/legal/matters/:id" element={<ProtectedRoute><LegalMatterDetail /></ProtectedRoute>} />
                <Route path="/app/legal/intake" element={<ProtectedRoute><LegalIntake /></ProtectedRoute>} />
                <Route path="/app/legal/intake/builder" element={<ProtectedRoute><LegalIntakeBuilder /></ProtectedRoute>} />
                <Route path="/app/legal/analytics" element={<ProtectedRoute><LegalAnalytics /></ProtectedRoute>} />
                <Route path="/app/legal/templates" element={<ProtectedRoute><LegalTemplates /></ProtectedRoute>} />
                <Route path="/app/legal/time" element={<ProtectedRoute><LegalTime /></ProtectedRoute>} />
                <Route path="/app/legal/supervisor" element={<ProtectedRoute><LegalSupervisor /></ProtectedRoute>} />
                <Route path="/app/legal/portal-admin" element={<ProtectedRoute><LegalPortalAdmin /></ProtectedRoute>} />
                <Route path="/app/legal/outcomes" element={<ProtectedRoute><LegalOutcomes /></ProtectedRoute>} />
                <Route path="/app/legal/doc-intelligence" element={<ProtectedRoute><LegalDocIntelligence /></ProtectedRoute>} />
                <Route path="/app/legal/time/payroll" element={<ProtectedRoute><LegalTimePayroll /></ProtectedRoute>} />
                <Route path="/app/legal/doccs-lookup" element={<ProtectedRoute><DoccsLookup /></ProtectedRoute>} />
                <Route path="/app/legal/handwritten" element={<ProtectedRoute><HandwrittenNotes /></ProtectedRoute>} />
                <Route path="/app/adoption" element={<ProtectedRoute><AdoptionCenter /></ProtectedRoute>} />
                <Route path="/app/admin/m365" element={<ProtectedRoute requireAdmin><M365Admin /></ProtectedRoute>} />

                {/* External Portal (no auth required) */}
                <Route path="/portal/invite/:token" element={<ExternalPortalPage />} />

                {/* Catch-all */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
